import {
  BILLING_FREQUENCY_ENUM,
  COUNTRY_CODE,
  EMAIL_REGX,
  STATUS_ENUM,
  BEAM_USER,
  CATEGORY_PRICING_TYPES,
  SCOPE_TYPE_ENUM,
  INITIAL_FORM_DATA
} from '../Constants/Constants';

export const isValidPostalCode = (postalCode, countryCode) => {
  if (!postalCode || !countryCode) return false;
  let postalCodeRegex;
  switch (countryCode) {
    case 'US':
      postalCodeRegex = /^([0-9]{5})(?:[-\s]*([0-9]{4}))?$/;
      break;
    case 'CA':
      postalCodeRegex = /^([A-Z][0-9][A-Z])\s*([0-9][A-Z][0-9])$/;
      break;
    default:
      postalCodeRegex = /^(?:[A-Z0-9]+([- ]?[A-Z0-9]+)*)?$/;
  }
  return postalCodeRegex.test(postalCode);
};

export const isMobileDevice = () => {
  const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];

  return toMatch.some(toMatchItem => {
    return navigator.userAgent.match(toMatchItem);
  });
};

// Update contract

// set enterprise attributes
export const setInitialFormData = ({
  email = '',
  enterpriseAttributes = {},
  platformAccess = {},
  accountSegment = INITIAL_FORM_DATA.account_segment,
  companyVertical = INITIAL_FORM_DATA.company_vertical,
  keyAccountManager = INITIAL_FORM_DATA.key_account_manager,
  onboardingSpecialist = INITIAL_FORM_DATA.onboarding_specialist
}) => {
  const {
    reports_given = [],
    ignore_marketing_mailer = true,
    ignore_draft_limit = true,
    ignore_size_limit = true,
    max_size_limit = 0,
    restrict_report_switching = true,
    restrict_custom_report_creation = true,
    ignore_concurrency_limit = false,
    show_optional_feature = false,
    concurrency_value = 2,
    min_measurement_size_unit = 0,
    apply_role_level_restriction = false,
    subdomain = '',
    callback_url = '',
    concurrency_value_construction = 2,
    is_pilot = false,
    default_report = {}
  } = enterpriseAttributes;

  const { end_date = '' } = platformAccess || {};

  return {
    email,
    end_date,
    reports_given,
    default_report: default_report?.id ?? '',
    ignore_marketing_mailer,
    ignore_draft_limit,
    ignore_size_limit,
    max_size_limit,
    restrict_report_switching,
    restrict_custom_report_creation,
    ignore_concurrency_limit,
    show_optional_feature,
    concurrency_value,
    min_measurement_size_unit,
    apply_role_level_restriction,
    subdomain,
    callback_url,
    concurrency_value_construction,
    is_pilot,
    account_segment: accountSegment,
    company_vertical: companyVertical,
    key_account_manager: keyAccountManager,
    onboarding_specialist: onboardingSpecialist
  };
};

export const setContractInfoData = ({ enterpriseContract, billingDetails }) => {
  const {
    start_date = '',
    end_date = '',
    billing_frequency = BILLING_FREQUENCY_ENUM.QUATERLY,
    aerial_contract_amount = '',
    per_acre_cost = '',
    remeasurement_contract_amount = '',
    remeasurement_per_acre_cost = '',
    bpt_contract_amount = '',
    workable_sheet_cost = '',
    addendum_sheet_cost = '',
    manual_worksheet_cost = '',
    manual_per_acre_cost = '',
    manual_threshold_acres = '',
    trade_wise_pricing = { others: { [CATEGORY_PRICING_TYPES.FRESH]: 0, [CATEGORY_PRICING_TYPES.ADDENDUM]: 0 } }
  } = enterpriseContract || {};

  const { email = '', name = '', address = '', city = '', zip = '', country = COUNTRY_CODE.USA } = billingDetails || {};

  return {
    billing_email: email,
    company_name: name,
    address,
    city,
    zip,
    country,
    start_date: start_date ? start_date?.split('T')[0] : '',
    end_date: end_date ? end_date?.split('T')[0] : '',
    billing_frequency,
    aerial_contract_amount,
    per_acre_cost,
    remeasurement_contract_amount,
    remeasurement_per_acre_cost,
    bpt_contract_amount,
    workable_sheet_cost,
    addendum_sheet_cost,
    manual_worksheet_cost,
    manual_per_acre_cost,
    manual_threshold_acres,
    trade_wise_pricing
  };
};

export const getStatus = status => {
  let [color, text, variant] = ['', '', ''];

  switch (status) {
    case STATUS_ENUM.CANCELLED_BY_CLIENT:
      color = 'red';
      variant = 'filled';
      text = 'CANCELLED BY CLIENT';
      break;
    case STATUS_ENUM.WAITING_FOR_CLIENT:
      color = 'orange';
      variant = 'filled';
      text = 'WAITING FOR CLIENT';
      break;
    case STATUS_ENUM.ESTIMATION:
      color = 'darkBlue';
      variant = 'filled';
      text = 'ESTIMATION';
      break;
    case STATUS_ENUM.IN_PROGRESS:
      color = 'gold';
      variant = 'filled';
      text = 'IN PROGRESS';
      break;
    case STATUS_ENUM.COMPLETED:
      color = 'green';
      variant = 'filled';
      text = 'COMPLETED';
      break;
    case STATUS_ENUM.TO_DO:
      color = 'neutral';
      variant = 'filled';
      text = 'TO DO';
      break;
    default:
      color = 'orange';
      variant = 'darkFilled';
      text = 'EMPTY';
  }
  return [color, variant, text];
};

export const downloadFileFromUrl = fileUrl => {
  const aTag = document.createElement('a');
  aTag.href = fileUrl;
  aTag.download = `file-${new Date().getTime()}`;
  aTag.click();
  aTag.remove();
};

export const isValidEmail = email => (email ? EMAIL_REGX.test(email) : true);

export const maskApiToken = token => {
  if (!token || typeof token !== 'string') return '';
  if (token.length <= 14) return token;
  return `${token.slice(0, 10)}...${token.slice(-4)}`;
};

export const isBeamUser = () => {
  const domain = window.location.hostname.split('.');
  return domain.includes(BEAM_USER);
};

export const formatDate = (_date, format = 'YYYY-MM-DD') => {
  const date = new Date(_date);
  const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`;
  return formattedDate;
};

export const dollarToCents = dollar => {
  return Math.round(dollar ? parseFloat(dollar) * 100 : 0);
};

export const centsToDollar = cents => {
  return cents ? parseFloat(cents) / 100 : 0;
};

export const transformContractData = (contractData, conversionType = 'dollarToCents') => {
  const conversionFunction = conversionType === 'dollarToCents' ? dollarToCents : centsToDollar;
  return {
    ...contractData,
    addendum_sheet_cost: conversionFunction(contractData.addendum_sheet_cost),
    bpt_contract_amount: conversionFunction(contractData.bpt_contract_amount),
    manual_worksheet_cost: conversionFunction(contractData.manual_worksheet_cost),
    manual_per_acre_cost: conversionFunction(contractData.manual_per_acre_cost),
    remeasurement_contract_amount: conversionFunction(contractData.remeasurement_contract_amount),
    remeasurement_per_acre_cost: conversionFunction(contractData.remeasurement_per_acre_cost),
    workable_sheet_cost: conversionFunction(contractData.workable_sheet_cost),
    aerial_contract_amount: conversionFunction(contractData.aerial_contract_amount),
    per_acre_cost: conversionFunction(contractData.per_acre_cost),
    manual_threshold_acres: conversionFunction(contractData.manual_threshold_acres),
    trade_wise_pricing: transformTradeWisePricing(contractData?.trade_wise_pricing || {}, conversionType)
  };
};

export const transformTradeWisePricing = (tradeWisePricing, conversionType = 'dollarToCents') => {
  const conversionFunction = conversionType === 'dollarToCents' ? dollarToCents : centsToDollar;
  return Object.fromEntries(
    Object.entries(tradeWisePricing || {}).map(([key, value]) => [
      key,
      Object.fromEntries(Object.entries(value).map(([key, value]) => [key, conversionFunction(value)]))
    ])
  );
};

// Rosetta Pricing Functions

// Helper function to calculate price per sheet for a trade based on selected scopes
export const calculateTradePrice = (trade, selectedScopeIds) => {
  if (!trade || !selectedScopeIds) {
    return trade?.base_price_per_sheet || 0;
  }

  const basePrice = trade.base_price_per_sheet;
  const maxBasicPrice = trade.max_basic_price_per_sheet;
  const maxAdvancedPrice = trade.max_advanced_price_per_sheet;
  const curveFactor = trade.pricing_curve_factor;

  const basicPriceRange = maxBasicPrice - basePrice;
  const advancedPriceRange = maxAdvancedPrice - maxBasicPrice;

  let totalBasicWeight = 0;
  let totalAdvancedWeight = 0;
  let selectedBasicWeight = 0;
  let selectedAdvancedWeight = 0;

  // Calculate weights for all scopes in this trade
  trade.scopes.forEach(scope => {
    if (!scope.is_default_selected) {
      if (scope.scope_type === SCOPE_TYPE_ENUM.BASIC) {
        totalBasicWeight += scope.price_weight;
        if (selectedScopeIds.includes(scope.scope_id)) {
          selectedBasicWeight += scope.price_weight;
        }
      } else if (scope.scope_type === SCOPE_TYPE_ENUM.ADVANCED) {
        totalAdvancedWeight += scope.price_weight;
        if (selectedScopeIds.includes(scope.scope_id)) {
          selectedAdvancedWeight += scope.price_weight;
        }
      }
    }
  });

  // Calculate final price based on selected scopes
  if (selectedAdvancedWeight > 0) {
    // If any advanced scopes are selected, use advanced pricing
    const advancedWeightRatio = totalAdvancedWeight > 0 ? selectedAdvancedWeight / totalAdvancedWeight : 0;
    const advancedPriceAddition = Math.pow(advancedWeightRatio, curveFactor) * advancedPriceRange;
    return maxBasicPrice + advancedPriceAddition;
  } else {
    // If only basic scopes are selected, use basic pricing
    const basicWeightRatio = totalBasicWeight > 0 ? selectedBasicWeight / totalBasicWeight : 0;
    const basicPriceAddition = Math.pow(basicWeightRatio, curveFactor) * basicPriceRange;
    return basePrice + basicPriceAddition;
  }
};

// Helper function to calculate blended price
export const calculateBlendedPrice = (tradesData, totalSheets) => {
  if (!tradesData || tradesData.length === 0 || totalSheets === 0) {
    return 0;
  }

  let totalCost = 0;
  let totalWeight = 0;

  tradesData.forEach(trade => {
    if (trade.scope_ids && trade.scope_ids.length > 0) {
      // Calculate weighted average for this trade
      const tradeWeight = trade.sheet_count || 0;
      totalWeight += tradeWeight;

      totalCost += (trade.trade_cost || 0) * tradeWeight;
    }
  });

  return totalWeight > 0 ? totalCost / totalWeight : 0;
};

// Helper function to get volume discount
export const getVolumeDiscount = (totalSheets, volumeDiscountTiers) => {
  if (!volumeDiscountTiers || totalSheets === 0) {
    return 0;
  }

  // Convert object to array of tiers and sort by sheet count (descending)
  const tiers = Object.entries(volumeDiscountTiers)
    .map(([sheetCount, discount]) => ({
      minSheets: parseInt(sheetCount),
      discount: discount / 100 // Convert percentage to decimal
    }))
    .sort((a, b) => b.minSheets - a.minSheets);

  // Find applicable tier
  for (const tier of tiers) {
    if (totalSheets >= tier.minSheets) {
      return tier.discount;
    }
  }
  return 0;
};
